import { isRouteAllowedByPermission } from '@/router/permissions'
import { showInfoMessage } from '@/notification-utils'
import { firstAllowedRoute } from '@/store/account-permissions'

/**
 * Keeps the signed-in member's permissions in step with what the server says, without a reload.
 *
 * Another admin can change a member's role while that member has the app open. The permissions come
 * from GET customerresources/user, which is only read when the app loads, so the member would keep a
 * menu - and pages - the server has already stopped honouring until they reloaded the page. This
 * re-reads the user when
 *
 * - the browser tab becomes visible again: the member has been away, which is when a change is most
 *   likely to have happened, and
 * - an API call is refused with a 403: the server has just told us the UI is out of date.
 *
 * and then re-evaluates. The store getters behind the menus update themselves - a member who gained
 * permissions sees the new entries at once - and a member who is sitting on a page they can no
 * longer open is sent to the dashboard.
 *
 * NO LOOPS, by three separate guards, because a 403 is exactly what an unlucky refresh could itself
 * get back: a 403 on the user request is never a reason to refresh; only one refresh runs at a time;
 * and after any refresh a 403 cannot start another for FORBIDDEN_REFRESH_INTERVAL_IN_MS.
 */

// A burst of 403s - a page whose requests are all refused - is one refresh, not one each.
export const FORBIDDEN_REFRESH_INTERVAL_IN_MS = 30 * 1000

// Coming back to the tab is a deliberate act, so it is allowed to refresh far more eagerly than a
// 403 is; the floor only stops a tab that flaps between visible and hidden from refreshing each time.
export const VISIBLE_REFRESH_INTERVAL_IN_MS = 5 * 1000

const USER_DETAILS_PATH = '/customerresources/user'

/**
 * @param {{ store: import('vuex').Store, router: import('vue-router').default, now?: () => number }} dependencies
 */
export function createPermissionRefresher({ store, router, now = () => Date.now() }) {
  let lastRefreshStartedAt = null
  let inFlight = null

  const isDue = interval => lastRefreshStartedAt === null || now() - lastRefreshStartedAt >= interval

  // Only a member whose user has loaded has permissions to be out of date - and the login flow is
  // not ours to interrupt.
  const canRefresh = () => store.getters['auth/isAuthenticated'] && store.getters['auth/haveUserData']

  // A member who has lost the permission the current page needs is sent to the dashboard, with a
  // reason: the page just going away would look like a fault.
  function leavePageIfNoLongerAllowed() {
    const route = router.currentRoute

    if (!isRouteAllowedByPermission(route, store.getters['auth/hasPermission'])) {
      showInfoMessage('Your access to that page was changed by an account admin.')

      router.replace({ name: firstAllowedRoute(store.getters['auth/hasPermission'], store.getters['auth/isBuyerAccount']) })
    }
  }

  async function run() {
    try {
      await store.dispatch('auth/refreshLoggedInUserDetails')
    } catch (error) {
      // Not worth interrupting the member for: the next tab visit or 403 tries again, and a member
      // whose own role was just changed is already treated as having no permissions - see
      // `permissionsUnverified`.
      console.error('Could not refresh the permissions of the signed-in user', error)

      return false
    }

    leavePageIfNoLongerAllowed()

    return true
  }

  /**
   * Re-reads the user now, unless nobody is signed in or a refresh is already running (that one is
   * handed back). Never rejects.
   */
  function refresh() {
    if (!canRefresh()) {
      return Promise.resolve(false)
    }

    if (inFlight) {
      return inFlight
    }

    lastRefreshStartedAt = now()

    inFlight = run().then(result => {
      inFlight = null

      return result
    })

    return inFlight
  }

  const refresher = {
    refresh,

    /** The tab has just become visible. */
    onVisible() {
      if (isDue(VISIBLE_REFRESH_INTERVAL_IN_MS)) {
        return refresh()
      }

      return Promise.resolve(false)
    },

    /**
     * A request was answered with a 403. `error` is the axios error.
     *
     * The refresh request's own 403 never starts another refresh - see the guards above.
     */
    onForbidden(error) {
      const url = error?.config?.url ?? ''

      if (url.endsWith(USER_DETAILS_PATH) || !isDue(FORBIDDEN_REFRESH_INTERVAL_IN_MS)) {
        return Promise.resolve(false)
      }

      return refresh()
    },

    /**
     * Listens for the tab coming back to the foreground. Returns the function that stops listening.
     * @param {Document} doc
     */
    listenForVisibility(doc = document) {
      const onVisibilityChange = () => {
        if (doc.visibilityState === 'visible') {
          refresher.onVisible()
        }
      }

      doc.addEventListener('visibilitychange', onVisibilityChange)

      return () => doc.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }

  return refresher
}

let appRefresher = null

/**
 * Starts the app's refresher. Called once from main.js, with the real store and router - they are
 * handed in rather than imported because the http client, which calls `onForbiddenResponse`, is
 * itself imported by the store, and importing back the other way would make a cycle.
 */
export function startPermissionRefresh({ store, router }) {
  appRefresher = createPermissionRefresher({ store, router })

  const stopVisibility = appRefresher.listenForVisibility()
  const stopWatch = store.watch(
    (state, getters) => JSON.stringify([getters['auth/accountId'], getters['auth/accountPermissions']]),
    () => {
      if (store.getters['auth/isAuthenticated'] &&
          !isRouteAllowedByPermission(router.currentRoute, store.getters['auth/hasPermission'])) {
        router.replace({ name: firstAllowedRoute(store.getters['auth/hasPermission'], store.getters['auth/isBuyerAccount']) })
      }
    }
  )
  return () => { stopVisibility(); stopWatch() }
}

/** For the http client's error interceptor: tells the refresher that a request was refused. */
export function onForbiddenResponse(error) {
  appRefresher?.onForbidden(error)
}
